import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useRef } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PageHeader } from '@/components/page-header'
import { SLIDING_ROW_CLASS, useSlidingRow } from '@/hooks/use-sliding-row'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
})

const SECTIONS = [
  { to: '/settings/profile', label: 'Profil' },
  { to: '/settings/notifications', label: 'Notifications' },
  { to: '/settings/api-keys', label: 'Clés API' },
  // 1.71.0 : les créneaux que les clients réservent au portail.
  { to: '/settings/booking', label: 'Disponibilités' },
  // Le suivi des envois porte des adresses personnelles : admin seulement.
  { to: '/settings/emails', label: 'Courriels', admin: true },
  // Le paramétrage des soumissions engage ce que les clients lisent : admin seulement.
  { to: '/settings/quotes', label: 'Soumissions', admin: true },
  // 1.85.0 : un membre rédige les conditions générales ; publier reste admin, sur la page.
  { to: '/settings/terms', label: 'Conditions générales' },
  // 1.65.0 : l'application Oyenga chez Google engage tous les comptes : admin seulement.
  { to: '/settings/integrations', label: 'Intégrations', admin: true },
] as const

function SettingsLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const { isAdmin } = useAuth()
  const sections = SECTIONS.filter((section) => !('admin' in section) || isAdmin)
  // Six sections ne tiennent plus sur un téléphone : la rangée glisse au
  // doigt, comme une `TabsList`, au lieu d'élargir la page.
  const navRef = useRef<HTMLElement>(null)
  const edges = useSlidingRow(navRef, '[aria-current="page"]', [pathname])

  return (
    <div className="space-y-6">
      <PageHeader title="Paramètres" />

      <nav
        ref={navRef}
        aria-label="Sections des paramètres"
        data-edges={edges}
        className={cn('flex gap-1 border-b border-border', SLIDING_ROW_CLASS)}
      >
        {sections.map((section) => {
          // Meta (lot 43) vit sous les intégrations : l'onglet reste allumé.
          const active = pathname === section.to || pathname.startsWith(`${section.to}/`)
          return (
            <Link
              key={section.to}
              to={section.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative inline-flex h-9 shrink-0 items-center rounded-t-md px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full',
                active
                  ? 'text-foreground after:bg-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {section.label}
            </Link>
          )
        })}
      </nav>

      <Outlet />
    </div>
  )
}
